/**
 * Palettes et utilitaires de thème backoffice — source unique pour le configurateur et BackofficeThemeService.
 */

export const DEFAULT_PRIMARY_SHADES: Record<string, string> = {
  '50': '#f7f1f7', '100': '#e7d0e7', '200': '#d6add6', '300': '#c389c3', '400': '#a95fa8',
  '500': '#541554', '600': '#4a124a', '700': '#3f0f3f', '800': '#340d34', '900': '#290a29', '950': '#180418'
};

export const DEFAULT_HELP_SHADES: Record<string, string> = {
  '50': '#fffbeb', '100': '#fef3c7', '200': '#fde68a', '300': '#fcd34d', '400': '#fbbf24',
  '500': '#f9c800', '600': '#d4ad12', '700': '#b45309', '800': '#92400e', '900': '#78350f', '950': '#451a03'
};

/** Couleur principale : 11 palettes distinctes et modernes (défaut = Violet) */
export const PRIMARY_PALETTES: Array<{ id: string; label: string; shades: Record<string, string> }> = [
  { id: 'violet', label: 'Violet', shades: { ...DEFAULT_PRIMARY_SHADES } },
  {
    id: 'violet-prune',
    label: 'Violet prune',
    shades: {
      '50': '#faf5ff', '100': '#f3e8ff', '200': '#e9d5ff', '300': '#d8b4fe', '400': '#c084fc',
      '500': '#9333ea', '600': '#7e22ce', '700': '#6b21a8', '800': '#581c87', '900': '#3b0764', '950': '#2e1065'
    }
  },
  {
    id: 'violet-moderne',
    label: 'Violet moderne',
    shades: {
      '50': '#f5f3ff', '100': '#ede9fe', '200': '#ddd6fe', '300': '#c4b5fd', '400': '#a78bfa',
      '500': '#7c3aed', '600': '#6d28d9', '700': '#5b21b6', '800': '#4c1d95', '900': '#3b0764', '950': '#2e1065'
    }
  },
  {
    id: 'blue-nuit',
    label: 'Bleu nuit',
    shades: {
      '50': '#e6eaf0', '100': '#bfc9dc', '200': '#95a5c4', '300': '#6a7fa9', '400': '#455d8c',
      '500': '#1e3a5f', '600': '#1a3151', '700': '#152843', '800': '#0f1f36', '900': '#0a1628', '950': '#050d18'
    }
  },
  {
    id: 'blue-fonce',
    label: 'Bleu foncé',
    shades: {
      '50': '#e8eef8', '100': '#c5d4ed', '200': '#9eb5e0', '300': '#7290d0', '400': '#4a6fbd',
      '500': '#1e4a9a', '600': '#1a3f82', '700': '#15346b', '800': '#102954', '900': '#0b1e3d', '950': '#061328'
    }
  },
  {
    id: 'blue-indigo',
    label: 'Bleu indigo',
    shades: {
      '50': '#eef2ff', '100': '#e0e7ff', '200': '#c7d2fe', '300': '#a5b4fc', '400': '#818cf8',
      '500': '#4f46e5', '600': '#4338ca', '700': '#3730a3', '800': '#312e81', '900': '#1e1b4b', '950': '#0f0d29'
    }
  },
  {
    id: 'blue-ciel',
    label: 'Bleu ciel',
    shades: {
      '50': '#f0f9ff', '100': '#e0f2fe', '200': '#bae6fd', '300': '#7dd3fc', '400': '#38bdf8',
      '500': '#0ea5e9', '600': '#0284c7', '700': '#0369a1', '800': '#075985', '900': '#0c4a6e', '950': '#082f49'
    }
  },
  {
    id: 'black',
    label: 'Noir',
    shades: {
      '50': '#f4f4f5', '100': '#e4e4e7', '200': '#d4d4d8', '300': '#a1a1aa', '400': '#71717a',
      '500': '#27272a', '600': '#1f1f23', '700': '#18181b', '800': '#121214', '900': '#0a0a0b', '950': '#020203'
    }
  },
  {
    id: 'graphite',
    label: 'Graphite',
    shades: {
      '50': '#f9fafb', '100': '#f3f4f6', '200': '#e5e7eb', '300': '#d1d5db', '400': '#9ca3af',
      '500': '#374151', '600': '#1f2937', '700': '#111827', '800': '#0f172a', '900': '#030712', '950': '#010305'
    }
  },
  {
    id: 'slate',
    label: 'Ardoise',
    shades: {
      '50': '#f8fafc', '100': '#f1f5f9', '200': '#e2e8f0', '300': '#cbd5e1', '400': '#94a3b8',
      '500': '#64748b', '600': '#475569', '700': '#334155', '800': '#1e293b', '900': '#0f172a', '950': '#020617'
    }
  }
];

/** Couleur secondaire (help) : dégradé du plus clair au plus foncé */
export const HELP_PALETTES: Array<{ id: string; label: string; color: string; hover: string; text: string }> = [
  { id: 'amber', label: 'Ambre', color: '#f9c800', hover: '#d4ad12', text: '#ffffff' },
  { id: 'ambre-orange', label: 'Ambre-orange', color: '#f59e0b', hover: '#d97706', text: '#ffffff' },
  { id: 'orange', label: 'Orange', color: '#f97316', hover: '#ea580c', text: '#ffffff' },
  { id: 'abricot', label: 'Abricot', color: '#ea580c', hover: '#c2410c', text: '#ffffff' }
];

// ——— Utilitaires (une seule implémentation) ———

export function isValidHexColor(value: string): boolean {
  return /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(value);
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const value = hex.replace('#', '').trim();
  const normalized = value.length === 3 ? value.split('').map(c => c + c).join('') : value;
  if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) return null;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16)
  };
}

export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (c: number) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function mixWithWhite(base: { r: number; g: number; b: number }, amount: number): string {
  return rgbToHex(
    base.r + (255 - base.r) * amount,
    base.g + (255 - base.g) * amount,
    base.b + (255 - base.b) * amount
  );
}

export function mixWithBlack(base: { r: number; g: number; b: number }, amount: number): string {
  return rgbToHex(base.r * (1 - amount), base.g * (1 - amount), base.b * (1 - amount));
}

export function createHoverColor(color: string, amount: number): string {
  const base = hexToRgb(color);
  return base ? mixWithBlack(base, amount) : color;
}

/** Teinte orange en degrés HSL (min et max). Tout choix hors plage est ramené à cette plage. */
const ORANGE_HUE_MIN = 20;
const ORANGE_HUE_MAX = 55;

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
    h *= 360;
  }
  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h /= 360;
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hueToRgb(p, q, h + 1 / 3);
    g = hueToRgb(p, q, h);
    b = hueToRgb(p, q, h - 1 / 3);
  }
  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
}
function hueToRgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

/** Ramène une couleur dans la plage orange (teinte HSL 20–55°). Utiliser pour la couleur secondaire. */
export function clampToOrangeHue(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const clampedH = Math.max(ORANGE_HUE_MIN, Math.min(ORANGE_HUE_MAX, h));
  const out = hslToRgb(clampedH, s, l);
  return rgbToHex(out.r, out.g, out.b);
}

export function generatePrimaryShades(color: string): Record<string, string> {
  const base = hexToRgb(color) || { r: 84, g: 21, b: 84 };
  return {
    '50': mixWithWhite(base, 0.92),
    '100': mixWithWhite(base, 0.82),
    '200': mixWithWhite(base, 0.68),
    '300': mixWithWhite(base, 0.52),
    '400': mixWithWhite(base, 0.32),
    '500': rgbToHex(base.r, base.g, base.b),
    '600': mixWithBlack(base, 0.12),
    '700': mixWithBlack(base, 0.24),
    '800': mixWithBlack(base, 0.38),
    '900': mixWithBlack(base, 0.52),
    '950': mixWithBlack(base, 0.68)
  };
}
